import { useState, useEffect, useCallback } from "react";
import { api } from "@/services/api";

interface MonthRow { month: string; count: number; amount: number }
interface StageRow { stage: string; total: number; value: number }
interface RMRow { id: number; name: string; leads: number; deals: number; won_value: number }
interface InvRow { type: string; status: string; total: number; value: number }

export default function AdminReports() {
  const [months, setMonths] = useState<MonthRow[]>([]);
  const [stages, setStages] = useState<StageRow[]>([]);
  const [rms, setRms] = useState<RMRow[]>([]);
  const [inv, setInv] = useState<InvRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [m, s, r, i] = await Promise.all([
        api.get<MonthRow[]>("/reports/revenue-by-month?months=12"),
        api.get<StageRow[]>("/reports/deals-by-stage"),
        api.get<RMRow[]>("/reports/rm-performance"),
        api.get<InvRow[]>("/reports/inventory"),
      ]);
      setMonths(m); setStages(s); setRms(r); setInv(i);
    } catch (e: any) {
      setError(e?.message || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const maxAmount = Math.max(...months.map((m) => m.amount), 1);
  const totalRevenue = months.reduce((sum, m) => sum + m.amount, 0);

  return (
    <div className="space-y-6">
      {error && <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">{error}</div>}
      {loading && <p className="py-16 text-center text-foreground-600"><i className="ri-loader-4-line animate-spin"></i> Loading...</p>}

      {/* Revenue by month */}
      <div className="rounded-2xl bg-background-50 border border-background-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-base text-foreground-950">Revenue (last 12 months)</h3>
            <p className="text-sm text-foreground-600">Total: <span className="font-bold text-foreground-950">₹{totalRevenue.toLocaleString("en-IN")}</span></p>
          </div>
        </div>
        <div className="flex items-end gap-1.5 h-40">
          {months.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex items-end justify-center" style={{ height: "100%" }}>
                <div className="w-full max-w-8 rounded-t bg-primary-500 hover:bg-primary-600 transition-colors" style={{ height: `${(m.amount / maxAmount) * 100}%` }} title={`₹${m.amount.toLocaleString("en-IN")}`}></div>
              </div>
              <span className="text-[9px] text-foreground-500">{m.month.slice(2)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Deals by stage */}
        <div className="rounded-2xl bg-background-50 border border-background-200 p-5">
          <h3 className="font-heading text-base text-foreground-950 mb-4">Deals by Stage</h3>
          <div className="space-y-3">
            {stages.map((s) => (
              <div key={s.stage}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-foreground-700 capitalize">{s.stage}</span>
                  <span className="font-semibold text-foreground-950">{s.total} deals · ₹{Number(s.value).toLocaleString("en-IN")}</span>
                </div>
                <div className="h-2 rounded-full bg-background-100 overflow-hidden">
                  <div className="h-full rounded-full bg-accent-500" style={{ width: `${(s.total / Math.max(Math.max(...stages.map((x) => x.total), 1), 1)) * 100}%` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RM performance */}
        <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
          <h3 className="font-heading text-base text-foreground-950 px-5 py-4 border-b border-background-100">RM Performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left text-xs uppercase tracking-wider text-foreground-600">
                  <th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Leads</th>
                  <th className="px-4 py-2.5">Deals</th>
                  <th className="px-4 py-2.5">Won Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {rms.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2.5 font-medium text-foreground-950">{r.name}</td>
                    <td className="px-4 py-2.5 text-foreground-700">{r.leads}</td>
                    <td className="px-4 py-2.5 text-foreground-700">{r.deals}</td>
                    <td className="px-4 py-2.5 font-semibold text-foreground-950">₹{r.won_value.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
                {rms.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-foreground-600">No data.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Inventory */}
      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <h3 className="font-heading text-base text-foreground-950 px-5 py-4 border-b border-background-100">Project Inventory</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left text-xs uppercase tracking-wider text-foreground-600">
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Count</th>
                <th className="px-4 py-2.5">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {inv.map((r, i) => (
                <tr key={i}>
                  <td className="px-4 py-2.5 capitalize text-foreground-800">{r.type}</td>
                  <td className="px-4 py-2.5 capitalize text-foreground-700">{r.status}</td>
                  <td className="px-4 py-2.5 font-semibold text-foreground-950">{r.total}</td>
                  <td className="px-4 py-2.5 text-foreground-700">₹{r.value.toLocaleString("en-IN")}</td>
                </tr>
              ))}
              {inv.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-foreground-600">No data.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}